export interface ITaxRate {
  readonly taxRateId?: number;
  readonly guid?: string;
  readonly number?: number;
  readonly name: string;
  readonly importId?: string;
  readonly type: "Percent" | "Fixed" | "Tax Table";
  readonly disabled: boolean;
  readonly rate: number;
  readonly default: boolean;
  readonly takeoutTaxEnabled: boolean;
  readonly takeoutTaxRate?: number;
  readonly roundingOptions:
    | "Half Even Rounding"
    | "Half Up Rounding"
    | "Always Round Down"
    | "Always Round Up";
  readonly createdAt?: Date;
  readonly updatedAt?: Date;
}

export interface ITaxRateData {
  taxRateId?: number;
  guid?: string;
  number?: number;
  name: string;
  importId?: string;
  type?: "Percent" | "Fixed" | "Tax Table";
  disabled?: boolean;
  rate?: number;
  default?: boolean;
  takeoutTaxEnabled?: boolean;
  takeoutTaxRate?: number;
  roundingOptions?:
    | "Half Even Rounding"
    | "Half Up Rounding"
    | "Always Round Down"
    | "Always Round Up";
  createdAt?: Date;
  updatedAt?: Date;
}

export class TaxRate implements ITaxRate {
  readonly taxRateId?: number;
  readonly guid?: string;
  readonly number?: number;
  readonly name: string;
  readonly importId?: string;
  readonly type: "Percent" | "Fixed" | "Tax Table";
  readonly disabled: boolean;
  readonly rate: number;
  readonly default: boolean;
  readonly takeoutTaxEnabled: boolean;
  readonly takeoutTaxRate?: number;
  readonly roundingOptions:
    | "Half Even Rounding"
    | "Half Up Rounding"
    | "Always Round Down"
    | "Always Round Up";
  readonly createdAt?: Date;
  readonly updatedAt?: Date;

  constructor(data: ITaxRateData) {
    this.taxRateId = data.taxRateId;
    this.guid = data.guid;
    this.number = data.number;
    this.name = data.name;
    this.importId = data.importId;
    this.type = data.type ?? "Percent";
    this.disabled = data.disabled ?? false;
    this.rate = data.rate ?? 0;
    this.default = data.default ?? false;
    this.takeoutTaxEnabled = data.takeoutTaxEnabled ?? false;
    this.takeoutTaxRate = data.takeoutTaxRate;
    this.roundingOptions = data.roundingOptions ?? "Half Even Rounding";
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }

  toJSON(): ITaxRate {
    return {
      taxRateId: this.taxRateId,
      guid: this.guid,
      number: this.number,
      name: this.name,
      importId: this.importId,
      type: this.type,
      disabled: this.disabled,
      rate: this.rate,
      default: this.default,
      takeoutTaxEnabled: this.takeoutTaxEnabled,
      takeoutTaxRate: this.takeoutTaxRate,
      roundingOptions: this.roundingOptions,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
    };
  }

  static fromJSON(data: Record<string, unknown>): TaxRate {
    if (!data.name) throw new Error("TaxRate.fromJSON: name is missing.");

    // Helper function to convert Firestore timestamps to Date objects
    const convertTimestamp = (timestamp: any): Date | undefined => {
      if (!timestamp) return undefined;
      // Check if it's a Firestore Timestamp object
      if (timestamp && typeof timestamp.toDate === "function") {
        return timestamp.toDate();
      }
      // Check if it's already a Date object
      if (timestamp instanceof Date) {
        return timestamp;
      }
      // Try to parse as string/number
      return new Date(timestamp);
    };

    return new TaxRate({
      taxRateId: data.taxRateId as number | undefined,
      guid: data.guid as string | undefined,
      number: data.number as number | undefined,
      name: data.name as string,
      importId: data.importId as string | undefined,
      type: data.type as "Percent" | "Fixed" | "Tax Table" | undefined,
      disabled: data.disabled as boolean | undefined,
      rate: data.rate as number | undefined,
      default: data.default as boolean | undefined,
      takeoutTaxEnabled: data.takeoutTaxEnabled as boolean | undefined,
      takeoutTaxRate: data.takeoutTaxRate as number | undefined,
      roundingOptions: data.roundingOptions as
        | "Half Even Rounding"
        | "Half Up Rounding"
        | "Always Round Down"
        | "Always Round Up"
        | undefined,
      createdAt: convertTimestamp(data.createdAt),
      updatedAt: convertTimestamp(data.updatedAt),
    });
  }
}
